'use client';

import { getTabProductList } from '@/actions/product-tab';
import { LocaleAttribute } from '@/app/i18n/settings';
import { MSProductWithFullDetailWithVaraintPreset } from '@/util/query/product-query';
import { CUSTOMER_LEVEL } from '@prisma/client';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import ProductShowboxesCarousel from './product-showboxes-carousel';
import ProductShowboxesProductListLoader from './product-showboxes-product-list-loader';

interface ProductGroupItem {
    id: string;
    collectionHandle?: string;
    enableButton?: boolean;
    buttonAttributes?: any;
    [key: string]: any;
}

interface Props {
    productGroupItems: ProductGroupItem[];
    initialTab: number;
    initialProducts: MSProductWithFullDetailWithVaraintPreset[];
    initialAllProducts?: MSProductWithFullDetailWithVaraintPreset[][];
    productsGroupData: any;
    loc: LocaleAttribute;
    colorStyle: any;
    shopCurrency: string;
    level: CUSTOMER_LEVEL;
    text: any;
    priorityImage?: boolean;
    radiusStyle: any;
}

export default function ProductShowboxesProductListClient({
    productGroupItems,
    initialTab,
    initialProducts,
    initialAllProducts,
    productsGroupData,
    loc,
    colorStyle,
    shopCurrency,
    level,
    text,
    priorityImage,
    radiusStyle,
}: Props) {
    const searchParams = useSearchParams();
    const instanceKey = productsGroupData?.id
        ? String(productsGroupData.id)
        : '';
    const paramKey = instanceKey ? `t_${instanceKey}` : 't';

    const cache = useRef<
        Map<number, MSProductWithFullDetailWithVaraintPreset[]>
    >(
        new Map(
            initialAllProducts && initialAllProducts.length > 0
                ? initialAllProducts.map((products, i) => [i, products])
                : [[initialTab, initialProducts]],
        ),
    );
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [, forceUpdate] = useState(0);

    const getTabFromParams = useCallback(() => {
        const raw = searchParams?.get(paramKey);
        if (!raw) return 0;
        const n = parseInt(raw.replace(/[^0-9]/g, '').slice(0, 4), 10);
        if (!Number.isNaN(n) && n >= 0 && n < productGroupItems.length)
            return n;
        return 0;
    }, [searchParams, paramKey, productGroupItems.length]);

    const fetchAndCache = useCallback(
        async (index: number) => {
            if (cache.current.has(index)) return;
            const item = productGroupItems[index];
            if (!item?.collectionHandle) return;
            setLoading(true);
            try {
                const products = await getTabProductList(
                    item.collectionHandle,
                    loc,
                    level,
                );
                cache.current.set(index, products);
            } finally {
                setLoading(false);
                forceUpdate((n) => n + 1);
            }
        },
        [productGroupItems, loc, level],
    );

    useEffect(() => {
        const newTab = getTabFromParams();
        if (newTab === activeTab) return;
        setActiveTab(newTab);
        fetchAndCache(newTab);
    }, [searchParams]);

    const products = cache.current.get(activeTab) ?? [];

    if (loading && products.length === 0) {
        return (
            <div className="w-full py-[40px] px-[30px] sm:px-[120px]">
                <ProductShowboxesProductListLoader />
            </div>
        );
    }

    if (products.length === 0) return null;

    return (
        <div className="w-full py-[40px] px-[30px] sm:px-[120px]">
            <ProductShowboxesCarousel
                productGroupItems={productGroupItems}
                productList={products}
                priorityImage={priorityImage ?? false}
                colorStyle={colorStyle}
                loc={loc}
                shopCurrency={shopCurrency}
                level={level}
                text={text}
                groupId={activeTab}
                radiusStyle={radiusStyle}
            />
        </div>
    );
}
